import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  type AutocompleteInputChangeReason,
  Checkbox,
  CircularProgress,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { UserIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { GetMlsBrokersQuery } from '@graphql/__generated__/graphql';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompletePaper,
  AutocompleteTextField,
  useFilterAutocompleteOpen,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useLocale } from '@shared/hooks/locale';

const SEARCH_DEBOUNCE_MS = 275;

// Query brokers directly from mls_brokers (cross-tenant view)
const GET_MLS_BROKERS = gql(/* GraphQL */ `
  query GetMlsBrokers($selectedIds: [uuid!]!, $hasSelectedIds: Boolean!) {
    # Get selected brokers (only when IDs are provided)
    selected_brokers: mls_brokers(where: { id: { _in: $selectedIds } })
      @include(if: $hasSelectedIds) {
      id
      first_name
      last_name
      full_name
      profile_image_url
    }
    # Get initial list of brokers (alphabetically)
    initial_brokers: mls_brokers(order_by: [{ full_name: asc }], limit: 20) {
      id
      first_name
      last_name
      full_name
      profile_image_url
    }
  }
`);

// Search brokers using ilike on name fields
const SEARCH_MLS_BROKERS = gql(/* GraphQL */ `
  query SearchMlsBrokers($search: String!) {
    mls_brokers(
      where: {
        _or: [
          { full_name: { _ilike: $search } }
          { first_name: { _ilike: $search } }
          { last_name: { _ilike: $search } }
        ]
      }
      order_by: [{ full_name: asc }]
      limit: 20
    ) {
      id
      first_name
      last_name
      full_name
      profile_image_url
    }
  }
`);

// Use the generated type from the query
type RawBroker = GetMlsBrokersQuery['initial_brokers'][number];
type BrokerOption = RawBroker & { id: string; allIds: string[] };

// Type guard to filter out brokers without required id
const hasBrokerId = (broker: RawBroker): broker is RawBroker & { id: string } =>
  broker.id != null;

// Dedupe brokers by full_name — merge all IDs for the same name into one option
const dedupeBrokers = (
  brokers: (RawBroker & { id: string })[],
): BrokerOption[] => {
  const byName = new Map<string, BrokerOption>();
  for (const broker of brokers) {
    const key = (broker.full_name ?? '').toLowerCase();
    const existing = byName.get(key);
    if (existing) {
      if (!existing.allIds.includes(broker.id)) {
        existing.allIds.push(broker.id);
      }
    } else {
      byName.set(key, { ...broker, allIds: [broker.id] });
    }
  }
  return [...byName.values()];
};

interface MlsBrokerMultiSelectProps {
  onClose?: () => void;
  onChange: (value: string[]) => void;
  defaultValue: string[];
}

const MlsBrokerMultiSelect = ({
  onChange,
  onClose,
  defaultValue,
}: MlsBrokerMultiSelectProps) => {
  const { t } = useLocale();
  const open = useFilterAutocompleteOpen();
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<BrokerOption[]>([]);
  const [options, setOptions] = useState<BrokerOption[]>([]);

  const hasSelectedIds = defaultValue.length > 0;
  const { data, loading: defaultLoading } = useQuery(GET_MLS_BROKERS, {
    variables: {
      selectedIds: hasSelectedIds ? defaultValue : [],
      hasSelectedIds,
    },
  });

  const [searchBrokers, { loading: searchLoading }] = useLazyQuery(
    SEARCH_MLS_BROKERS,
    { notifyOnNetworkStatusChange: true },
  );

  // Update options from query results — dedupe by name across tenants
  const updateOptions = useCallback(
    (
      searchResults?: (RawBroker & { id: string })[],
      initialBrokers?: (RawBroker & { id: string })[],
    ) => {
      const combined = [...(searchResults ?? []), ...(initialBrokers ?? [])];
      setOptions(dedupeBrokers(combined));
    },
    [],
  );

  // Initialize options and selected from data
  useEffect(() => {
    if (data && options.length === 0 && !inputValue) {
      const rawSelected = (data.selected_brokers ?? []).filter(hasBrokerId);
      const rawInitial = (data.initial_brokers ?? []).filter(hasBrokerId);
      const allDeduped = dedupeBrokers([...rawSelected, ...rawInitial]);
      const selectedIdSet = new Set(defaultValue);
      const selected = allDeduped.filter(opt =>
        opt.allIds.some(id => selectedIdSet.has(id)),
      );
      setSelectedOptions(selected);
      setOptions(allDeduped);
    }
  }, [data, options.length, inputValue, defaultValue]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (searchValue: string) => {
        const { data: searchData } = await searchBrokers({
          variables: {
            search: `%${searchValue}%`,
          },
        });
        if (searchData?.mls_brokers) {
          setOptions(dedupeBrokers(searchData.mls_brokers.filter(hasBrokerId)));
        } else {
          setOptions([]);
        }
      }, SEARCH_DEBOUNCE_MS),
    [searchBrokers],
  );

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue) {
        debouncedSearch(searchValue);
      } else {
        // Reset to initial options
        updateOptions(
          (data?.selected_brokers ?? []).filter(hasBrokerId),
          (data?.initial_brokers ?? []).filter(hasBrokerId),
        );
      }
    },
    [
      data?.initial_brokers,
      data?.selected_brokers,
      debouncedSearch,
      updateOptions,
    ],
  );

  const handleToggleOptions = useCallback(
    (_event: SyntheticEvent, newValue: BrokerOption[]) => {
      setSelectedOptions(newValue);
      onChange(newValue.flatMap(v => v.allIds));
    },
    [onChange],
  );

  const handleInputChange = useCallback(
    (
      _: SyntheticEvent<Element, Event>,
      newValue: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      if (reason !== 'input') {
        return;
      }
      setInputValue(newValue);
      handleSearch(newValue);
    },
    [handleSearch],
  );

  const loading = defaultLoading || searchLoading;

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      openOnFocus
      open={open}
      disablePortal
      disableClearable
      size="small"
      clearOnBlur={false}
      value={selectedOptions}
      loading={loading}
      loadingText={t('Loading...')}
      onClose={onClose}
      sx={{ width: 350 }}
      options={options}
      getOptionLabel={option => option.full_name ?? ''}
      filterOptions={x => x}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isOptionEqualToValue={(option, value) =>
        (option.full_name ?? '').toLowerCase() ===
        (value.full_name ?? '').toLowerCase()
      }
      onChange={handleToggleOptions}
      renderInput={params => (
        <AutocompleteTextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option, { selected }) => {
        const avatarUrl = option.profile_image_url;
        return (
          <ListItem {...props} key={option.id} dense>
            <ListItemIcon sx={{ minWidth: 0 }}>
              <Checkbox
                edge="start"
                checked={selected}
                tabIndex={-1}
                disableRipple
              />
              <ListItemAvatar>
                <UserAvatar
                  user={{
                    first_name: option.first_name,
                    last_name: option.last_name,
                    user_images: avatarUrl
                      ? [{ image: { url: avatarUrl } }]
                      : [],
                  }}
                />
              </ListItemAvatar>
            </ListItemIcon>
            <ListItemText primary={option.full_name} />
          </ListItem>
        );
      }}
      noOptionsText={t('No options')}
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

export const MlsBrokerFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
}: QuickFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();

  const defaultValue = useMemo(
    () => getValueFromPath(path, where)?.id?._in ?? [],
    [getValueFromPath, path, where],
  );

  const handleChange = useCallback(
    (value: string[]) => {
      let newWhere = deleteWhereClause(where, path, true);

      if (value.length > 0) {
        newWhere = addWhereClause(newWhere, [...path, 'id', '_in'], value);
      }
      setFiltersParams(newWhere);
    },
    [deleteWhereClause, where, path, setFiltersParams, addWhereClause],
  );

  const handleDelete =
    defaultValue.length > 0 ? () => handleChange([]) : undefined;

  return (
    <FilterChip
      label={label}
      icon={<UserIcon size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <MlsBrokerMultiSelect
          onClose={handleClose}
          onChange={handleChange}
          defaultValue={defaultValue}
        />
      )}
    />
  );
};
